import { useQuery, useQueryClient } from '@tanstack/react-query'

import { streamRequest } from '@/lib/streamRequest'
import { readNdjsonStream } from '@/lib/ndjsonParser'
import useModelsFingerprint from '@/services/api/model/useModelsFingerprint'

/**
 * The cash flow grid's data source (PERF-001).
 *
 * Streams NDJSON period aggregates from `/team/cash-flow/transactions/stream` and
 * pushes each period into the query cache as its line arrives, so the first
 * columns paint before the last period lands. No transaction detail, no balance
 * arithmetic in the client — cells read `closing_balance` / `turnover` straight
 * off this feed.
 *
 * Each period:
 *   { offset, start, end, number, month, year,
 *     vaults:     { [vaultId]:    { opening_balance, amount_in, amount_out, closing_balance,
 *                                   estimated_amount_in, estimated_amount_out, estimated_closing_balance } },
 *     categories: { [categoryId]: { turnover, estimated_turnover } } }
 */
export default function useGetCashflowPeriods({
  offset,
  numberOfPeriods,
  periodType,
} = {}) {
  const queryClient = useQueryClient()

  // Toggling a What-If changes the numbers but not the view args — without this in
  // the key, staleTime:Infinity would serve the pre-toggle feed.
  const modelsFingerprint = useModelsFingerprint()

  // Namespaced under 'transactions' so every existing
  // invalidateQueries({ queryKey: ['transactions'] }) in the mutation hooks
  // refreshes the grid feed without touching 25 call sites.
  const queryKey = [
    'transactions',
    'periods',
    { offset, numberOfPeriods, periodType, modelsFingerprint },
  ]

  const fetchData = async ({ signal }) => {
    const response = await streamRequest(
      '/team/cash-flow/transactions/stream',
      { o: offset, c: numberOfPeriods, p: periodType },
      { signal },
    )

    const periods = []

    await readNdjsonStream(response.body, (raw) => {
      periods.push(normalizePeriod(raw))
      // Progressive paint: publish a snapshot after every line.
      queryClient.setQueryData(queryKey, [...periods])
    })

    return periods
  }

  return useQuery({
    queryKey,
    queryFn: fetchData,
    staleTime: Infinity,
  })
}

function normalizePeriod(raw) {
  const vaults = {}
  for (const vault of raw.vaults ?? []) {
    vaults[vault.vault_id] = {
      opening_balance: vault.opening_balance,
      amount_in: vault.amount_in,
      amount_out: vault.amount_out,
      closing_balance: vault.closing_balance,
      estimated_amount_in: vault.estimated_amount_in,
      estimated_amount_out: vault.estimated_amount_out,
      estimated_closing_balance: vault.estimated_closing_balance,
    }
  }

  const categories = {}
  for (const category of raw.categories ?? []) {
    categories[category.transaction_category_id] = {
      turnover: category.turnover,
      estimated_turnover: category.estimated_turnover,
    }
  }

  return {
    offset: raw.offset,
    start: raw.start,
    end: raw.end,
    number: raw.number,
    month: raw.month,
    year: raw.year,
    vaults,
    categories,
  }
}
